// =============================================================================
// Portal "My Design Detail" Page — library design + Spawn Product
// -----------------------------------------------------------------------------
// Surfaces a single MERCHANT_PRODUCT_LIBRARY design and lets the merchant
// spawn a NEW product from it (re-using the same design payload — no
// designer launch needed).
//
//   • GET /api/v1/designs/:id — full designJson + bound UPT + spawnSummary
//   • POST /api/v1/designs/:id/spawn-product — atomic Product create
//     (Idempotency-Key wired through; server enforces tenant + library
//     context guards).
//   • DELETE /api/v1/designs/:id — library-only, blocked by 409 when
//     spawnSummary.productCount > 0.
// =============================================================================

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Badge, Button, Card, CardHeader, Input, Spinner } from '@tkpod/ui';
import { useApiClient } from '@tkpod/api-client';
import { SpawnProductDialog } from '../components/SpawnProductDialog';

interface SpawnSummary {
  productCount: number;
  lastSpawnedAt: string | null;
}

interface LibraryDesignDetail {
  id: string;
  designRefId: string | null;
  title: string | null;
  status: string;
  context: string;
  previewUrl: string | null;
  uptId: string | null;
  upt: { id: string; name: string; slug: string } | null;
  designJson: Record<string, unknown> | null;
  spawnSummary: SpawnSummary | null;
  createdAt: string;
  updatedAt: string;
}

export function MyDesignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [spawnOpen, setSpawnOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['portal', 'my-designs', id],
    queryFn: () =>
      api.get<{ data: LibraryDesignDetail } | LibraryDesignDetail>(
        `/api/v1/designs/${id}`,
      ),
    enabled: !!id,
  });

  const raw = detailQuery.data as unknown as
    | { data?: LibraryDesignDetail }
    | LibraryDesignDetail
    | undefined;
  const design: LibraryDesignDetail | undefined =
    (raw as { data?: LibraryDesignDetail } | undefined)?.data ??
    (raw as LibraryDesignDetail | undefined);

  const deleteMutation = useMutation({
    mutationFn: () => api.delete<void>(`/api/v1/designs/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'my-designs'] });
      navigate('/my-designs');
    },
  });

  const productCount = design?.spawnSummary?.productCount ?? 0;
  const canDelete = productCount === 0;

  const sidesSummary = useMemo<string>(() => {
    const sides = design?.designJson?.['sides'];
    if (!Array.isArray(sides)) return 'No sides';
    return `${sides.length} side${sides.length === 1 ? '' : 's'}`;
  }, [design?.designJson]);

  if (detailQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }
  if (detailQuery.isError) {
    return (
      <div className="py-24 text-center">
        <p className="text-sm text-danger mb-3">Failed to load design.</p>
        <Button variant="outline" size="sm" onClick={() => detailQuery.refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  if (!design?.id) {
    return (
      <div className="py-24 text-center text-sm text-text-tertiary">
        Design not found.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border bg-surface px-4 py-3 sm:px-5">
        <div className="flex items-center gap-4 min-w-0">
          <button
            type="button"
            onClick={() => navigate('/my-designs')}
            className="flex items-center justify-center rounded-md border border-border bg-surface p-2 text-text-secondary transition-colors hover:bg-surface-secondary hover:text-text-primary"
            aria-label="Back to My Designs"
            title="Back to My Designs"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-lg font-bold text-text-primary sm:text-xl">
                {design.title ?? 'Untitled design'}
              </h1>
              <Badge variant="info" size="sm">
                Library
              </Badge>
              {productCount > 0 && (
                <Badge variant="default" size="sm">
                  Spawned {productCount} product{productCount === 1 ? '' : 's'}
                </Badge>
              )}
            </div>
            <p className="mt-0.5 truncate font-mono text-[11px] text-text-tertiary">
              {design.designRefId ?? '—'} · Updated{' '}
              {new Date(design.updatedAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setSpawnOpen(true)}
            disabled={!design.uptId}
          >
            Create product from this design
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmDeleteOpen(true)}
            disabled={!canDelete || deleteMutation.isPending}
            title={
              canDelete
                ? 'Permanently delete this library design'
                : 'Delete the spawned products first'
            }
          >
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        {/* Left — preview + meta */}
        <div className="space-y-4">
          <Card padding="none">
            <CardHeader title="Preview" />
            <div className="flex justify-center px-4 pb-4 sm:px-5 sm:pb-5">
              <div className="relative aspect-square w-full max-w-md overflow-hidden rounded border border-border bg-surface-secondary">
                {design.previewUrl ? (
                  <img
                    src={design.previewUrl}
                    alt={design.title ?? design.designRefId ?? 'Design preview'}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-xs text-text-tertiary">
                    <Spinner size="sm" />
                    Preview is being generated.
                  </div>
                )}
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title="Design info" />
            <div className="grid grid-cols-2 gap-3 px-4 pb-4 sm:px-5 sm:pb-5 text-xs">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary mb-1">
                  Template
                </p>
                <p className="text-text-primary">{design.upt?.name ?? '—'}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary mb-1">
                  Sides
                </p>
                <p className="text-text-primary">{sidesSummary}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary mb-1">
                  Created
                </p>
                <p className="text-text-primary">
                  {new Date(design.createdAt).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary mb-1">
                  Last edited
                </p>
                <p className="text-text-primary">
                  {new Date(design.updatedAt).toLocaleString()}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Right — usage */}
        <div className="space-y-4">
          <Card>
            <CardHeader title="Usage" />
            <div className="space-y-2 px-4 pb-4 sm:px-5 sm:pb-5 text-sm">
              <p className="text-text-primary">
                {productCount === 0
                  ? 'No products yet.'
                  : `Spawned in ${productCount} product${productCount === 1 ? '' : 's'}.`}
              </p>
              {design.spawnSummary?.lastSpawnedAt && (
                <p className="text-[11px] text-text-tertiary">
                  Last spawned{' '}
                  {new Date(design.spawnSummary.lastSpawnedAt).toLocaleString()}
                </p>
              )}
              {productCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => navigate('/products?source=design')}
                >
                  View spawned products
                </Button>
              )}
            </div>
          </Card>

          {deleteMutation.isError && (
            <Card>
              <CardHeader title="Delete failed" />
              <div className="px-4 pb-4 sm:px-5 sm:pb-5 text-sm">
                <p className="text-danger text-xs">
                  {(deleteMutation.error as Error)?.message ?? 'Unknown error'}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => deleteMutation.reset()}
                >
                  Dismiss
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Spawn dialog */}
      {spawnOpen && design.uptId && (
        <SpawnProductDialog
          libraryDesignId={design.id}
          uptId={design.uptId}
          defaultTitle={design.title ?? 'Untitled design'}
          onClose={() => setSpawnOpen(false)}
          onSuccess={(productId) => {
            setSpawnOpen(false);
            queryClient.invalidateQueries({
              queryKey: ['portal', 'my-designs', id],
            });
            // Land on the freshly spawned product so the merchant
            // immediately sees the variant grid + render progress.
            navigate(`/products/${productId}`);
          }}
        />
      )}

      {/* Inline delete confirmation */}
      {confirmDeleteOpen && (
        <ConfirmDeleteDialog
          designTitle={design.title ?? 'this design'}
          onCancel={() => setConfirmDeleteOpen(false)}
          onConfirm={() => {
            setConfirmDeleteOpen(false);
            deleteMutation.mutate();
          }}
          isPending={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

function ConfirmDeleteDialog({
  designTitle,
  onCancel,
  onConfirm,
  isPending,
}: {
  designTitle: string;
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  const [typed, setTyped] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-surface p-5 shadow-xl">
        <h2 className="text-base font-semibold text-text-primary">
          Delete design?
        </h2>
        <p className="mt-2 text-sm text-text-secondary">
          This will permanently remove "{designTitle}" from your library. You
          can&apos;t undo this. Type <strong>DELETE</strong> to confirm.
        </p>
        <Input
          className="mt-3"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="DELETE"
          aria-label="Confirm delete"
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={typed !== 'DELETE' || isPending}
            onClick={onConfirm}
          >
            {isPending ? 'Deleting…' : 'Delete design'}
          </Button>
        </div>
      </div>
    </div>
  );
}
